import { lazy } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';

import Layout from './Layout/Layout';
import Login from './Login/Login';
import Jobs from './Jobs/Jobs';

const Dashboard = lazy(() => import('./Dashboard/Dashboard'));
const Agent = lazy(() => import('./Agents/Agents'));
const AgentViewPage = lazy(() => import('./Agents/view/page'));
const Workflows = lazy(() => import('./Workflows/Workflows'));
const Settings = lazy(() => import('./Settings/Settings'));
const Merchants = lazy(() => import('./Settings/Merchants/Merchants'));
const MerchantCreate = lazy(() => import('./Settings/Merchants/create/page'));
const MerchantView = lazy(() => import('./Settings/Merchants/view/page'));
const Users = lazy(() => import('./Settings/Users/Users'));
const UserCreate = lazy(() => import('./Settings/Users/create/page'));
const UserView = lazy(() => import('./Settings/Users/view/page'));
const DocumentUpload = lazy(() => import('./Settings/DocumentUpload/DocumentUpload'));
const FundAllocation = lazy(() => import('./Settings/FundAllocation/FundAllocation'));
const FundAllocationCreate = lazy(
  () =>
    import(
      './Settings/FundAllocation/components/FundAllocation/components/CreateFundAllocation/page'
    )
);
const FundCreate = lazy(
  () => import('./Settings/FundAllocation/components/FundMaster/create/page')
);
const FundView = lazy(() => import('./Settings/FundAllocation/components/FundMaster/view/page'));
const ConfigCreate = lazy(
  () => import('./Settings/FundAllocation/components/Configuration/create/page')
);
const ConfigView = lazy(
  () => import('./Settings/FundAllocation/components/Configuration/view/page')
);
const WorkflowView = lazy(() => import('./Workflows/view/page'));
// const Jobs = lazy(() => import(/* webpackPrefetch: true */ './Jobs/Jobs'));
const JobsCreate = lazy(() => import('./Jobs/create/page'));
const JobsLayout = lazy(() => import('./Jobs/jobslayout/jobslayout'));
const JobDetails = lazy(() => import('./Jobs/details/page'));
const JobDashboard = lazy(() => import('./Jobs/dashboard/page'));
const Assistant = lazy(() => import('./Jobs/assistant/page'));

export default function AllRoutes() {
  const token = useSelector((state) => _get(state, 'auth.token', ''));
  const selectedLoanTypeID = useSelector((state) =>
    _get(state, 'auth.user.selected_loan_type.id', '')
  );
  const isLoggedIn = !!token;

  const router = createBrowserRouter([
    {
      path: '/',
      element: !isLoggedIn ? (
        <Navigate to='/login' replace />
      ) : (
        <Navigate to='/dashboard' replace />
      ),
    },
    {
      path: '/login',
      element: !isLoggedIn ? <Login /> : <Navigate to='/dashboard' replace />,
    },
    {
      path: '/',
      element: !isLoggedIn ? (
        <Navigate to='/login' replace />
      ) : (
        <Layout selectedLoanTypeID={selectedLoanTypeID} />
      ),
      children: [
        { path: 'dashboard', element: <Dashboard /> },

        { path: 'agents', element: <Agent /> },
        { path: 'agents/view/:agent_id', element: <AgentViewPage /> },

        { path: 'jobs', element: <Jobs /> },
        { path: 'jobs/create', element: <JobsCreate /> },
        {
          path: 'jobs',
          element: <JobsLayout />,
          children: [
            { path: 'details/:job_id', element: <JobDetails /> },
            { path: 'dashboard', element: <JobDashboard /> },
            { path: 'assistant', element: <Assistant /> },
          ],
        },

        { path: 'workflows', element: <Workflows /> },
        { path: 'workflows/view/:workflow_id', element: <WorkflowView /> },

        { path: 'settings', element: <Settings /> },

        { path: 'settings/merchants', element: <Merchants /> },
        { path: 'settings/merchants/create', element: <MerchantCreate /> },
        { path: 'settings/merchants/view/:merchant_id', element: <MerchantView /> },
        { path: 'settings/merchants/edit/:merchant_id', element: <MerchantCreate /> },

        { path: 'settings/users/create', element: <UserCreate /> },
        { path: 'settings/users/view/:user_id', element: <UserView /> },
        { path: 'settings/users/edit/:user_id', element: <UserCreate /> },
        { path: 'settings/users', element: <Users /> },

        { path: 'settings/document-upload', element: <DocumentUpload /> },

        { path: 'settings/fund-allocation', element: <FundAllocation /> },
        { path: 'settings/fund-allocation/create', element: <FundAllocationCreate /> },

        { path: 'settings/fund-master/create', element: <FundCreate /> },
        { path: 'settings/fund-master/edit/:fund_id', element: <FundCreate /> },
        { path: 'settings/fund-master/view/:fund_id', element: <FundView /> },

        { path: 'settings/configuration/create', element: <ConfigCreate /> },
        { path: 'settings/configuration/edit/:config_id', element: <ConfigCreate /> },
        { path: 'settings/configuration/view/:config_id', element: <ConfigView /> },

        { path: 'profile/:user_id', element: <UserView /> },
        { path: 'profile/edit/:user_id', element: <UserCreate /> },
      ],
    },
    {
      path: '*',
      element: <div>Page Not Found</div>,
    },
  ]);

  return <RouterProvider router={router} />;
}
